import { proxy } from "valtio";
import { Job } from "@models/job.model";
import { CommandGetJobStatus } from "@services/jobs/commands/cmdGetJobStatus";

const POLL_INTERVAL_MS = 1000;
const COMPLETED_DISMISS_MS = 5000;
const FAILED_DISMISS_MS = 8000;
const TERMINAL_STATUSES = ["COMPLETED", "FAILED", "COMPLETED_WITH_ERRORS", "WARNING"];

type JobLike = { status: string; children?: readonly { status: string }[] };

export function isJobTerminal(job: JobLike): boolean {
  if (!TERMINAL_STATUSES.includes(job.status)) return false;
  if (!job.children?.length) return true;
  return job.children.every((c) => TERMINAL_STATUSES.includes(c.status));
}

type TrackedJob = Job & { dismissing?: boolean };

export const jobTrackerStore = proxy<{
  activeJobs: TrackedJob[];
}>({
  activeJobs: [],
});

let pollInterval: ReturnType<typeof setInterval> | null = null;

function startPolling() {
  if (pollInterval) return;
  pollInterval = setInterval(pollJobs, POLL_INTERVAL_MS);
}

function stopPolling() {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
}

async function pollJobs() {
  const cmd = new CommandGetJobStatus();
  const jobs = [...jobTrackerStore.activeJobs];

  for (const job of jobs) {
    if (job.dismissing) continue;
    try {
      const updated = await cmd.run(job.id);
      const idx = jobTrackerStore.activeJobs.findIndex((j) => j.id === job.id);
      if (idx === -1) continue;

      jobTrackerStore.activeJobs[idx].status = updated.status;
      jobTrackerStore.activeJobs[idx].progress_message = updated.progress_message;
      jobTrackerStore.activeJobs[idx].percent_complete = updated.percent_complete;
      jobTrackerStore.activeJobs[idx].children = updated.children;

      if (isJobTerminal(updated)) {
        jobTrackerStore.activeJobs[idx].dismissing = true;
        const delay = updated.status === "COMPLETED" ? COMPLETED_DISMISS_MS : FAILED_DISMISS_MS;
        setTimeout(() => removeJob(job.id), delay);
      }
    } catch {
      // If polling fails, leave the job as-is; next tick will retry
    }
  }

  if (jobTrackerStore.activeJobs.length === 0) {
    stopPolling();
  }
}

export function addJob(job: Job) {
  jobTrackerStore.activeJobs.push({ ...job });
  startPolling();
}

export function removeJob(jobId: number) {
  const idx = jobTrackerStore.activeJobs.findIndex((j) => j.id === jobId);
  if (idx !== -1) {
    jobTrackerStore.activeJobs.splice(idx, 1);
  }
  if (jobTrackerStore.activeJobs.length === 0) {
    stopPolling();
  }
}
